import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Task, TaskStatus, Track } from "../../lib/types";
import { TASK_STATUSES } from "../../lib/types";
import { useUI } from "../../lib/store";
import { kanban, projects, tasks, tracks } from "../../lib/api";
import { AttachTaskModal } from "../tracks/AttachTaskModal";
import { TrackEditor } from "../tracks/TrackEditor";
import { Modal } from "../shared/Modal";
import { statusLabel, statusRailColor } from "../shared/StatusDot";
import { TaskEditModal } from "./TaskEditModal";
import { DoneReviewModal } from "./DoneReviewModal";
import { BufferIndicator } from "../shared/BufferIndicator";
import { Chip } from "../shared/Card";
import { Check, ShieldCheck } from "../shared/Icon";
import {
  ContextMenu,
  CtxMenuItem,
  CtxMenuSeparator,
  CtxMenuSub,
} from "../shared/Menu";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { cn } from "../../lib/cn";
import { elapsed, formatCost, formatTokens } from "../../lib/format";

interface Props {
  task: Task;
}

export function TaskCard({ task }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id });
  const qc = useQueryClient();
  const selectTask = useUI((s) => s.setSelectedTaskId);
  const setMode = useUI((s) => s.setRightPaneMode);
  const rightPaneMode = useUI((s) => s.rightPaneMode);
  const setDrawer = useUI((s) => s.setRightPaneOpenMobile);
  const revealPane = useUI((s) => s.setRightPaneVisible);
  const selectedTaskId = useUI((s) => s.selectedTaskId);

  const [editing, setEditing] = useState(false);
  const [confirmHard, setConfirmHard] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);

  // Keep the draft in sync when the task title changes externally (another
  // surface edited it, or a refetch overwrote our optimistic state). Don't
  // overwrite while the operator is actively renaming.
  useEffect(() => {
    if (!renaming) setTitleDraft(task.title);
  }, [task.title, renaming]);

  const batchSelection = useUI((s) => s.batchSelection);
  const toggleBatch = useUI((s) => s.toggleBatchSelection);
  const trackSelection = useUI((s) => s.trackSelection);
  const toggleTrack = useUI((s) => s.toggleTrackSelection);

  // Whether this task's project runs the autonomous loops. Drives which triage
  // pills show: the untriaged/triaging states are only meaningful here, so we
  // hide them on cards in projects that don't use the loops. Shared cache key
  // with the rest of the app — one fetch covers every card.
  const { data: allProjects } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projects.list(),
  });
  const ownerProject = allProjects?.find((p) => p.id === task.project_id);
  const loopEnabled = !!(
    ownerProject?.manager_loop_enabled || ownerProject?.worker_loop_enabled
  );

  const selected = selectedTaskId === task.id;
  const detail = cardDetail(task.title, task.description);
  const isRunning = task.status === "in_progress" || task.status === "verifying";
  const isPaused = task.status === "needs_input";
  const isLive = isRunning || isPaused;
  // Only In-Review cards can be batch-confirmed together (Phase 2).
  const batchable = task.status === "in_review";
  const batchSelected = batchSelection.includes(task.id);
  const trackSelected = trackSelection.includes(task.id);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  } as React.CSSProperties;

  const openTask = () => {
    selectTask(task.id);
    // Don't yank the user out of whichever tab they prefer — only force
    // a tab if they're on Chat (which isn't task-scoped). Default to
    // Activity since the operator usually opens a card to read what the
    // agent is doing, not the raw stdout.
    if (rightPaneMode === "chat") setMode("activity");
    revealPane(true);
    setDrawer(true);
  };

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["kanban"] });

  const archive = useMutation({
    mutationFn: () => tasks.archive(task.id),
    onSuccess: invalidate,
  });
  const hardDelete = useMutation({
    mutationFn: () => tasks.hardDelete(task.id),
    onSuccess: (result) => {
      invalidate();
      setConfirmHard(false);
      const sync = result?.pending_sync;
      if (sync && sync.status !== "ok" && sync.status !== "skipped") {
        // eslint-disable-next-line no-alert
        alert(
          `Task deleted, but PENDING.md sync had an issue:\n\n${sync.message}`,
        );
      }
    },
  });
  const dispatch = useMutation({
    mutationFn: () => tasks.dispatch(task.id),
    onSuccess: invalidate,
  });
  const retry = useMutation({
    mutationFn: () => tasks.retry(task.id),
    onSuccess: invalidate,
    onError: (e) => {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });
  const cancel = useMutation({
    mutationFn: () => tasks.cancel(task.id),
    onSuccess: invalidate,
  });
  const triage = useMutation({
    mutationFn: () => tasks.triage(task.id),
    onSuccess: invalidate,
    onError: (e) => {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });
  const routeTask = useMutation({
    mutationFn: (s: "agent_ready" | "needs_human") => tasks.route(task.id, s),
    onSuccess: invalidate,
  });
  const duplicate = useMutation({
    mutationFn: async () => {
      const copy = await tasks.create({
        project_id: task.project_id,
        title: `${task.title} (copy)`,
        description: task.description,
        prompt: task.prompt,
        status: "todo",
        autonomy_level: task.autonomy_level,
        model: task.model,
        spec_mode: task.spec_mode,
      });
      return copy;
    },
    onSuccess: (created) => {
      invalidate();
      selectTask(created.task.id);
    },
  });
  const move = useMutation({
    mutationFn: (status: TaskStatus) => kanban.move(task.id, status),
    onSuccess: invalidate,
    onError: (e) => {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });
  const rename = useMutation({
    mutationFn: (title: string) => tasks.update(task.id, { title }),
    onSuccess: invalidate,
    onError: (e) => {
      setTitleDraft(task.title);
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });

  const submitRename = () => {
    const next = titleDraft.trim();
    if (next && next !== task.title) {
      rename.mutate(next);
    } else {
      setTitleDraft(task.title);
    }
    setRenaming(false);
  };
  const cancelRename = () => {
    setTitleDraft(task.title);
    setRenaming(false);
  };
  const [reviewOpen, setReviewOpen] = useState(false);
  const [attachTrack, setAttachTrack] = useState<Track | null>(null);
  const [creatingTrack, setCreatingTrack] = useState(false);
  const requestMove = (status: TaskStatus) => {
    // Moving to `done` always routes through the review modal so the
    // universal done-flow (PENDING/CHANGELOG round-trip) can fire.
    if (status === "done") {
      setReviewOpen(true);
      return;
    }
    move.mutate(status);
  };

  const dispatchable = task.status === "todo" || task.status === "backlog";

  // "Add to track" is offered only for un-started, un-tracked, live cards.
  // The track list is shared by queryKey, so all eligible cards in a project
  // resolve to a single fetch.
  const eligibleForTrack = dispatchable && !task.track_id;
  const { data: projectTracks = [] } = useQuery({
    queryKey: ["tracks", task.project_id],
    queryFn: () => tracks.list(task.project_id),
    enabled: eligibleForTrack,
  });

  // A move to in_progress only makes sense if there's a captured CLI
  // session to resume — otherwise the row becomes a "phantom in_progress"
  // (status says running, no agent is actually running). The Dispatch
  // path is the right surface for starting fresh. Backend enforces the
  // same gate; this disables the menu entry up-front so the operator
  // sees the reason before they click.
  const resumeAvailable = !!task.cli_session_id;
  const phantomInProgressBlocked = (s: TaskStatus) =>
    s === "in_progress" && !resumeAvailable;
  const phantomTooltip = "No saved CLI session — click Dispatch instead";

  const renderContextItems = () => (
    <>
      <CtxMenuItem onSelect={() => setEditing(true)}>Edit</CtxMenuItem>
      <CtxMenuItem onSelect={() => dispatch.mutate()} disabled={!dispatchable}>
        {task.started_at ? "Re-dispatch" : "Dispatch"}
      </CtxMenuItem>
      {task.status === "interrupted" && (
        <CtxMenuItem
          onSelect={() => retry.mutate()}
          title={`Interrupted ${task.attempt_count}× so far`}
        >
          Retry
        </CtxMenuItem>
      )}
      <CtxMenuItem onSelect={() => duplicate.mutate()}>Duplicate</CtxMenuItem>
      <CtxMenuSub trigger="Triage…">
        <CtxMenuItem onSelect={() => triage.mutate()}>
          Re-classify (risk + type)
        </CtxMenuItem>
        <CtxMenuSeparator />
        <CtxMenuItem
          onSelect={() => routeTask.mutate("agent_ready")}
          disabled={
            task.triage_status === "agent_ready" && task.triaged_by === "human"
          }
          title={
            task.triage_status === "agent_ready" && task.triaged_by !== "human"
              ? "Confirm as human-approved — lets the worker claim it in shadow mode"
              : undefined
          }
        >
          {task.triage_status === "agent_ready" && task.triaged_by !== "human"
            ? "Confirm agent-ready (human)"
            : "Route → agent-ready"}
        </CtxMenuItem>
        <CtxMenuItem
          onSelect={() => routeTask.mutate("needs_human")}
          disabled={task.triage_status === "needs_human"}
        >
          Route → needs human
        </CtxMenuItem>
      </CtxMenuSub>
      {eligibleForTrack && (
        <CtxMenuSub trigger="Add to track…">
          <CtxMenuItem onSelect={() => setCreatingTrack(true)}>New track…</CtxMenuItem>
          {projectTracks.length > 0 && <CtxMenuSeparator />}
          {projectTracks.map((tr) => (
            <CtxMenuItem key={tr.id} onSelect={() => setAttachTrack(tr)}>
              {tr.title}
            </CtxMenuItem>
          ))}
        </CtxMenuSub>
      )}
      <CtxMenuSub trigger="Move to…">
        {TASK_STATUSES.filter((s) => s !== task.status).map((s) => {
          const blocked = phantomInProgressBlocked(s);
          return (
            <CtxMenuItem
              key={s}
              onSelect={() => requestMove(s)}
              disabled={blocked}
              title={blocked ? phantomTooltip : undefined}
            >
              {statusLabel(s)}
            </CtxMenuItem>
          );
        })}
      </CtxMenuSub>
      {isLive && (
        <CtxMenuItem onSelect={() => cancel.mutate()}>Cancel</CtxMenuItem>
      )}
      <CtxMenuSeparator />
      <CtxMenuItem onSelect={() => archive.mutate()} disabled={isLive}>
        Archive
      </CtxMenuItem>
      <CtxMenuItem
        variant="destructive"
        onSelect={() => setConfirmHard(true)}
        disabled={isLive}
      >
        Delete permanently
      </CtxMenuItem>
    </>
  );

  return (
    <>
      <ContextMenu items={renderContextItems()}>
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          onClick={openTask}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openTask();
            }
          }}
          role="button"
          tabIndex={0}
          aria-pressed={selected}
          className={cn(
            "group relative bg-bg-surface rounded-md",
            "border transition-all duration-fast ease-out",
            "cursor-grab active:cursor-grabbing",
            "pl-4 pr-3.5 pt-3.5 pb-3",
            "hover:bg-bg-hover hover:border-border-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas",
            selected
              ? "border-accent-muted bg-accent-soft"
              : "border-border-subtle",
            (batchSelected || trackSelected) && "ring-2 ring-accent border-accent",
          )}
        >
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 w-[3px] rounded-l-md"
            style={{ background: statusRailColor(task.status) }}
          />
          <div className="flex items-start gap-2">
            {batchable && (
              <button
                type="button"
                role="checkbox"
                aria-checked={batchSelected}
                aria-label="Select for confirm-together"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBatch(task.id);
                }}
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center",
                  "transition-colors duration-fast ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  batchSelected
                    ? "bg-accent border-accent text-accent-contrast"
                    : "border-border-default text-transparent hover:border-border-strong",
                )}
              >
                <span className="h-3 w-3">
                  <Check />
                </span>
              </button>
            )}
            {eligibleForTrack && (
              <button
                type="button"
                role="checkbox"
                aria-checked={trackSelected}
                aria-label="Select to add to a track"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTrack(task.id);
                }}
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center",
                  "transition-colors duration-fast ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  trackSelected
                    ? "bg-accent border-accent text-accent-contrast"
                    : "border-border-default text-transparent hover:border-border-strong opacity-0 group-hover:opacity-100",
                )}
              >
                <span className="h-3 w-3">
                  <Check />
                </span>
              </button>
            )}
            <div className="min-w-0 flex-1">
              {renaming ? (
                <input
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={submitRename}
                  onFocus={(e) => e.currentTarget.select()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      submitRename();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      cancelRename();
                    } else {
                      e.stopPropagation();
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className={cn(
                    "w-full text-small font-semibold leading-snug",
                    "bg-bg-canvas text-text-primary",
                    "border border-accent-muted rounded-sm px-1 py-0.5 -mx-1 -my-0.5",
                    "focus:outline-none focus:ring-1 focus:ring-accent",
                  )}
                  aria-label="Rename task"
                />
              ) : (
                <div
                  className="text-small font-semibold text-text-primary line-clamp-2 leading-snug"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setTitleDraft(task.title);
                    setRenaming(true);
                  }}
                  title="Double-click to rename"
                >
                  {task.priority && <PriorityBadge priority={task.priority} />}
                  {task.risk && <RiskBadge risk={task.risk} />}
                  {task.parent_task_id && (
                    <span
                      className="inline-flex items-center h-[18px] px-1.5 mr-1.5 rounded-sm align-middle text-mono text-[11px]"
                      style={{
                        color: "var(--accent)",
                        background: "var(--accent-soft)",
                      }}
                      title="Spawned by another task — open to see lineage"
                    >
                      ↰ spawn
                    </span>
                  )}
                  {task.lifecycle === "shelved" && (
                    <span
                      className="inline-flex items-center h-[18px] px-1.5 mr-1.5 rounded-sm align-middle text-mono text-[11px]"
                      style={{ color: "var(--text-tertiary)", background: "var(--bg-inset)" }}
                      title="Shelved — deferred to a future sprint"
                    >
                      shelved
                    </span>
                  )}
                  {task.title}
                </div>
              )}
              {detail && !renaming && (
                // Title-first: the detail is collapsed by default and expands
                // inline on hover (or while the card is selected). Animating
                // the grid row from 0fr→1fr grows the card downward, so the
                // meta footer — and the date in it — is pushed down, never
                // covered. The detail is de-duped + markdown-stripped so it
                // reads as clean prose, not a repeat of the title.
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-base ease-out",
                    selected
                      ? "grid-rows-[1fr]"
                      : "grid-rows-[0fr] group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <div
                      className="text-small text-text-secondary pt-1.5 line-clamp-4 leading-relaxed"
                      title={detail}
                    >
                      {detail}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-2.5 pt-2 border-t border-border-subtle flex items-center gap-2 flex-wrap">
            <TriageBadge
              triageStatus={task.triage_status}
              loopEnabled={loopEnabled}
            />
            {task.task_type && <Chip>{task.task_type}</Chip>}
            {task.dispatch_origin === "loop" && (
              <span title="Auto-dispatched by the worker loop">
                <Chip>auto</Chip>
              </span>
            )}
            {task.agent_type && <Chip>{task.agent_type}</Chip>}
            {task.worktree_name && (
              <span title={task.worktree_name}>
                <Chip>{task.worktree_name}</Chip>
              </span>
            )}
            {task.tokens_used > 0 && (
              <span className="text-mono text-text-tertiary">
                {formatTokens(task.tokens_used)} tok
              </span>
            )}
            {Number(task.cost_usd) > 0 && (
              <span className="text-mono text-text-tertiary">
                {formatCost(task.cost_usd)}
              </span>
            )}
            {task.started_at && (
              <span className="text-mono text-text-tertiary">
                {elapsed(task.started_at, task.completed_at)}
              </span>
            )}
            <span
              className="text-mono text-text-tertiary ml-auto"
              title={`Created ${task.created_at}`}
            >
              {shortDate(task.created_at)}
            </span>
          </div>

          {task.status === "verifying" ? (
            // Maestro is checking the work (gates / revert check / review):
            // icon only — the shield carries the state.
            <span
              role="img"
              aria-label="Verifying"
              title="Verifying: Maestro is checking this work"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-accent motion-safe:animate-pulse"
            >
              <ShieldCheck size={18} />
            </span>
          ) : (
            isRunning && (
              <BufferIndicator
                size={20}
                label="agent running"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
              />
            )
          )}
        </div>
      </ContextMenu>
      {editing && (
        <TaskEditModal task={task} onClose={() => setEditing(false)} />
      )}
      {reviewOpen && (
        <DoneReviewModal task={task} onClose={() => setReviewOpen(false)} />
      )}
      {creatingTrack && (
        <Modal
          onClose={() => setCreatingTrack(false)}
          title="New track"
          size="lg"
        >
          <TrackEditor
            projectId={task.project_id}
            onCancel={() => setCreatingTrack(false)}
            onCreated={(created) => {
              setCreatingTrack(false);
              // Chain straight into the attach step so this task lands in the
              // track the operator just made — same modal as picking an
              // existing track, with this card preset.
              qc.invalidateQueries({ queryKey: ["tracks", task.project_id] });
              setAttachTrack(created);
            }}
          />
        </Modal>
      )}
      {attachTrack && (
        <AttachTaskModal
          track={attachTrack}
          presetTask={task}
          onClose={() => setAttachTrack(null)}
        />
      )}
      <ConfirmDialog
        open={confirmHard}
        onClose={() => setConfirmHard(false)}
        onConfirm={() => hardDelete.mutate()}
        title={`Delete "${task.title}" permanently?`}
        description="This permanently removes the task, its session log, and its cost events. This cannot be undone."
        confirmLabel="Delete permanently"
        variant="destructive"
        pending={hardDelete.isPending}
      />
    </>
  );
}

/**
 * Card descriptions are stored as markdown whose first sentence repeats the
 * title in bold — e.g. `**Slim the build: drop aws-lc-rs.** reqwest→hyper…`.
 * The board already shows the title as the headline, so we strip that
 * duplicated bold lead and flatten the remaining inline markdown, leaving
 * just the extra detail as clean prose for the hover reveal.
 */
function cardDetail(title: string, description?: string | null): string {
  if (!description) return "";
  let d = description.trim();

  // Strip a leading **bold sentence** when it's really the title repeated.
  // The bold can span newlines and carry a trailing `:`/`.`; we only strip it
  // if it matches the title (exactly, or as a truncation either way) so a
  // genuinely-different first sentence is never eaten.
  const lead = d.match(/^\*\*([\s\S]*?)\*\*[\s.:]*/);
  if (lead) {
    const norm = (s: string) =>
      s.replace(/\s+/g, " ").trim().replace(/[.:]+$/, "").toLowerCase();
    const boldText = norm(lead[1]);
    const titleNorm = norm(title);
    if (
      boldText === titleNorm ||
      boldText.startsWith(titleNorm) ||
      titleNorm.startsWith(boldText)
    ) {
      d = d.slice(lead[0].length);
    }
  }

  // Flatten remaining inline markdown so no raw markers show on the card.
  d = d
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/\s+/g, " ")
    .trim();

  return d;
}

/**
 * Compact created-at stamp. Same-year dates collapse to "May 25"; older
 * ones include the year. Operators read these to triage stale backlog.
 */
function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

const PRIORITY_PALETTE: Record<string, { fg: string; bg: string }> = {
  P0: { fg: "var(--status-error)", bg: "var(--status-error-soft)" },
  P1: { fg: "var(--status-paused)", bg: "var(--status-paused-soft)" },
  P2: { fg: "var(--status-running)", bg: "var(--status-running-soft)" },
  P3: { fg: "var(--text-tertiary)", bg: "var(--bg-inset)" },
};

export function PriorityBadge({ priority }: { priority: string }) {
  const colors = PRIORITY_PALETTE[priority] ?? PRIORITY_PALETTE.P3;
  return (
    <span
      className="inline-flex items-center h-[18px] px-1.5 mr-1.5 rounded-sm align-middle text-mono text-[11px]"
      style={{ color: colors.fg, background: colors.bg }}
      title={`Priority ${priority}`}
    >
      {priority}
    </span>
  );
}

// Risk uses semantic status colors — red for high, amber for medium, muted
// for low. Label-only (no icon) per the design rules.
const RISK_PALETTE: Record<string, { fg: string; bg: string }> = {
  high: { fg: "var(--status-error)", bg: "var(--status-error-soft)" },
  medium: { fg: "var(--status-warning)", bg: "var(--status-warning-soft)" },
  low: { fg: "var(--text-tertiary)", bg: "var(--bg-inset)" },
};

function RiskBadge({ risk }: { risk: string }) {
  const colors = RISK_PALETTE[risk] ?? RISK_PALETTE.low;
  return (
    <span
      className="inline-flex items-center h-[18px] px-1.5 mr-1.5 rounded-sm align-middle text-mono text-[11px]"
      style={{ color: colors.fg, background: colors.bg }}
      title={`Risk: ${risk}`}
    >
      {risk}
    </span>
  );
}

// Triage pill — label-only, semantic colors per the design rules. The two
// operator-actionable states (needs_human escalation, agent_ready) always show,
// since a human can route a task by hand even in a project without the loops.
// The pipeline states (untriaged, triaging) only mean something while the loops
// run, so they show only when this task's project has a loop enabled — that
// keeps an "untriaged" pill off every card in non-loop projects.
const TRIAGE_PILL: Record<
  Task["triage_status"],
  { label: string; fg: string; bg: string; title: string }
> = {
  needs_human: {
    label: "needs:human",
    fg: "var(--status-paused)",
    bg: "var(--status-paused-soft)",
    title: "Routed to a human — needs review before it can auto-run",
  },
  agent_ready: {
    label: "agent-ready",
    fg: "var(--status-success)",
    bg: "var(--status-success-soft)",
    title: "Classified agent-ready — eligible for the worker loop",
  },
  triaging: {
    label: "triaging",
    fg: "var(--status-running)",
    bg: "var(--status-running-soft)",
    title: "The manager loop is classifying this task",
  },
  untriaged: {
    label: "untriaged",
    fg: "var(--text-tertiary)",
    bg: "var(--bg-inset)",
    title: "Not yet classified — waiting for the manager loop",
  },
};

function TriageBadge({
  triageStatus,
  loopEnabled,
}: {
  triageStatus: Task["triage_status"];
  loopEnabled: boolean;
}) {
  const isPipeline = triageStatus === "untriaged" || triageStatus === "triaging";
  if (isPipeline && !loopEnabled) return null;
  const pill = TRIAGE_PILL[triageStatus];
  if (!pill) return null;
  return (
    <span
      className="inline-flex items-center h-[18px] px-1.5 rounded-sm text-mono text-[11px]"
      style={{ color: pill.fg, background: pill.bg }}
      title={pill.title}
    >
      {pill.label}
    </span>
  );
}
